import { useState, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { VideoPlayerState, VideoPlayerStateObject, ReactPlayerInstance } from '../types';
import { VideoPlayerContext } from '../components/VideoPlayerProvider/VideoPlayerProvider';
import { VideoItemReference } from 'types/sharedComponents';
import { useVideoInteractionTracking } from './useVideoInteractionTracking';
import { VIDEO_PLAYER_CONSTANTS } from '../constants';
import {
  getPlayerProvider,
  getEffectiveMuted,
  createReactPlayerConfig,
  cleanupYouTubeIframe,
  cleanupTikTokIframe,
} from '../utils';

interface UseVideoPlayerStateProps {
  video: VideoItemReference;
  autoPlay: boolean;
  playInBackground: boolean;
  onReady: (currentPlayerId: string) => void;
  onStart: (currentPlayerId: string) => void;
  onPlay: (currentPlayerId: string) => void;
  onPause: (currentPlayerId: string) => void;
  onEnded: (currentPlayerId: string) => void;
  // Additional props for optimizations
  testId: string;
  muted: boolean;
  loop: boolean;
  hideNativeControls: boolean;
  mergedClassNames: { wrapper: string; player: string };
}

export const useVideoPlayerState = ({
  video,
  autoPlay,
  playInBackground,
  onReady,
  onStart,
  onPlay,
  onPause,
  onEnded,
  // Additional props for optimizations
  testId,
  muted,
  loop,
  hideNativeControls,
  mergedClassNames,
}: UseVideoPlayerStateProps) => {
  const { registerPlayer, unregisterPlayer, notifyPlayerStarted } = useContext(VideoPlayerContext);
  const [playerRef, setPlayerRefState] = useState<ReactPlayerInstance | null>(null);
  const [playerState, setPlayerState] = useState<VideoPlayerState>(VideoPlayerState.Init);
  const [hasStarted, setHasStarted] = useState(false);
  const [isInEndingProcess, setIsInEndingProcess] = useState(false);
  const previousStateRef = useRef<VideoPlayerState | null>(null);

  // Get video URL and player ID - memoized to prevent unnecessary recalculations
  const videoUrl = useMemo(() => {
    // Try alternative videoUrl property first (for external URLs like YouTube)
    if (video?.videoUrl) {
      return video.videoUrl;
    }
    // Fall back to uploaded video asset
    if (!video?.video?.url) return null;
    return video.video.url;
  }, [video?.video?.url, video?.videoUrl]);

  // Derive player ID from testId to ensure uniqueness
  const playerId = useMemo(() => testId, [testId]);

  // Get video interaction tracking
  const { handleVideoInteraction } = useVideoInteractionTracking();

  // Memoize event handlers to prevent unnecessary re-renders
  const eventHandlers = useMemo(
    () => ({
      onReady: () => onReady(playerId),
      onStart: () => onStart(playerId),
      onPlay: () => onPlay(playerId),
      onPause: () => onPause(playerId),
      onEnded: () => onEnded(playerId),
    }),
    [playerId, onReady, onStart, onPlay, onPause, onEnded],
  );

  // Create memoized play/pause methods for the provider
  const playerMethods = useMemo(
    () => ({
      play: () => {
        setPlayerState(VideoPlayerState.Playing);
      },
      pause: () => {
        setPlayerState(VideoPlayerState.Paused);
      },
    }),
    [],
  );

  // React Player event handlers - these only change state
  const handleReady = useCallback(() => {
    setPlayerState(VideoPlayerState.Init);
  }, []);

  const handleStart = useCallback(() => {
    setPlayerState(VideoPlayerState.Starting);
    setHasStarted(true);
  }, []);

  const handlePlay = useCallback(() => {
    // When a video ends, YouTube automatically resets it to the beginning and triggers play/pause events.
    // We want to block these automatic state changes but allow legitimate user interactions.
    // The isInEndingProcess flag helps us distinguish between auto-reset and user actions.

    if (!isInEndingProcess) {
      setPlayerState(VideoPlayerState.Playing);
    }
  }, [isInEndingProcess]);

  const handlePause = useCallback(() => {
    // Only allow pause if the video has been started at least once
    // and we're not in the ending process (which would be auto-reset)
    if (hasStarted && !isInEndingProcess) {
      setPlayerState(VideoPlayerState.Paused);
    }
  }, [hasStarted, isInEndingProcess]);

  const handleEnded = useCallback(() => {
    // Video has ended - set the ending process flag to block auto-reset events
    setPlayerState(VideoPlayerState.Ended);
    setIsInEndingProcess(true);

    // Clear the ending process flag after a delay to allow legitimate user interactions.
    // This delay should be sufficient for YouTube's auto-reset behavior to complete.
    setTimeout(() => {
      setIsInEndingProcess(false);
    }, 1000); // 1 second delay
  }, []);

  // Memoize the player object to prevent unnecessary re-registrations
  const playerObject = useMemo(
    () => ({
      id: playerId,
      playInBackground,
      pause: playerMethods.pause,
      playerRef,
      playerState,
    }),
    [playerId, playInBackground, playerMethods.pause, playerRef, playerState],
  );

  // Register/unregister player with context
  useEffect(() => {
    if (playerRef) {
      registerPlayer(playerObject);

      return () => {
        unregisterPlayer(playerId);
      };
    }
  }, [playerObject, registerPlayer, unregisterPlayer, playerId, playerRef]);

  // Cleanup effect for YouTube and TikTok iframes to prevent orphaned iframe errors
  useEffect(() => {
    return () => {
      // Cleanup function that runs when component unmounts
      if (playerRef) {
        cleanupYouTubeIframe(playerRef);
        cleanupTikTokIframe(playerRef);
      }
    };
  }, [playerRef]);

  // Monitor state changes and call appropriate event handlers
  useEffect(() => {
    const previousState = previousStateRef.current;
    previousStateRef.current = playerState;

    // Only trigger events on state transitions, not on initial render
    if (previousState === null) {
      // First render - only log Ready event
      eventHandlers.onReady();
      handleVideoInteraction(playerRef, VideoPlayerState.Init, video);
      return;
    }

    switch (playerState) {
      case VideoPlayerState.Init:
        // Only call onReady if we're transitioning TO Init from a different state
        if (previousState !== VideoPlayerState.Init) {
          eventHandlers.onReady();
          handleVideoInteraction(playerRef, VideoPlayerState.Init, video);
        }
        break;
      case VideoPlayerState.Starting:
        eventHandlers.onStart();
        break;
      case VideoPlayerState.Playing:
        // Only notify provider if we're transitioning TO Playing from a different state
        if (previousState !== VideoPlayerState.Playing) {
          notifyPlayerStarted(playerId, playInBackground);
          // Then call the event handler
          eventHandlers.onPlay();
          handleVideoInteraction(playerRef, VideoPlayerState.Playing, video);
        }
        break;
      case VideoPlayerState.Paused:
        eventHandlers.onPause();
        handleVideoInteraction(playerRef, VideoPlayerState.Paused, video);
        break;
      case VideoPlayerState.Ended:
        eventHandlers.onEnded();
        handleVideoInteraction(playerRef, VideoPlayerState.Ended, video);
        break;
    }
  }, [
    playerState,
    playerId,
    playInBackground,
    eventHandlers,
    playerRef,
    handleVideoInteraction,
    video,
    notifyPlayerStarted,
  ]);

  // Effects for initialization and autoplay
  useEffect(() => {
    if (autoPlay && videoUrl && playerRef && !hasStarted) {
      // Add a small delay to ensure the player is fully ready
      const timer = setTimeout(() => {
        setPlayerState(VideoPlayerState.Playing);
        setHasStarted(true);
      }, VIDEO_PLAYER_CONSTANTS.DEFAULT_AUTOPLAY_DELAY);

      return () => clearTimeout(timer);
    }
  }, [autoPlay, videoUrl, playerRef, hasStarted]);

  // Additional effect to handle autoplay state synchronization
  useEffect(() => {
    if (autoPlay && hasStarted && playerState === VideoPlayerState.Playing) {
      // Ensure the player state is properly set when autoplay is active
      setPlayerState(VideoPlayerState.Playing);
    }
  }, [autoPlay, hasStarted, playerState]);

  useEffect(() => {
    if (playInBackground && hasStarted) {
      setPlayerState(VideoPlayerState.Playing);
    }
  }, [playInBackground, hasStarted]);

  // Optimized computed values and state objects
  const playerProvider = useMemo(() => getPlayerProvider(videoUrl || ''), [videoUrl]);
  const effectiveMuted = useMemo(() => getEffectiveMuted(muted, autoPlay), [muted, autoPlay]);
  const isPlaying = useMemo(() => {
    const playing = playerState === VideoPlayerState.Playing;
    return playing;
  }, [playerState]);

  // Memoized state objects for children - only recreate when dependencies actually change
  const videoPlayerState = useMemo<VideoPlayerStateObject>(
    () => ({
      playerState,
      testId,
      video,
      isPlaying,
      handlePlayClick: handlePlay,
      handlePauseClick: handlePause,
    }),
    [playerState, testId, video, isPlaying, handlePlay, handlePause],
  );

  // Create setPlayerRef function
  const setPlayerRef = useCallback((instance: ReactPlayerInstance | null) => {
    setPlayerRefState(instance);
  }, []);

  // ReactPlayer configuration - memoized to prevent unnecessary re-renders
  const playerConfig = useMemo(() => {
    if (!videoUrl) {
      return null;
    }

    const config = createReactPlayerConfig({
      src: videoUrl,
      playing: isPlaying,
      loop,
      muted: effectiveMuted,
      controls: !hideNativeControls, // Show native controls unless hideNativeControls is true
      className: mergedClassNames.player,
      testId: `${testId}-player`,
      onReady: handleReady,
      onStart: handleStart,
      onPlay: handlePlay,
      onPause: handlePause,
      onEnded: handleEnded,
      ref: setPlayerRef,
    });

    return config;
  }, [
    videoUrl,
    isPlaying,
    loop,
    effectiveMuted,
    hideNativeControls,
    mergedClassNames.player,
    testId,
    handleReady,
    handleStart,
    handlePlay,
    handlePause,
    handleEnded,
    setPlayerRef,
  ]);

  // Memoize the wrapper props to prevent unnecessary re-renders
  const wrapperProps = useMemo(
    () => ({
      'data-testid': `${testId}-wrapper`,
      className: mergedClassNames.wrapper,
      'data-video-player-state': playerState,
      'data-video-player-provider': playerProvider,
    }),
    [testId, mergedClassNames.wrapper, playerState, playerProvider],
  );

  // Memoize the viewport props
  const viewportProps = useMemo(
    () => ({
      'data-testid': `${testId}-viewport`,
      className: 'relative w-full h-full',
    }),
    [testId],
  );

  return {
    setPlayerRef: useCallback((instance: ReactPlayerInstance | null) => {
      setPlayerRefState(instance);
    }, []),
    playerId,
    // Optimized computed values and state objects
    playerProvider,
    effectiveMuted,
    isPlaying,
    videoPlayerState,
    playerConfig,
    wrapperProps,
    viewportProps,
  };
};
